// src/components/work/WorkViewSwitcher.jsx
// Tabs (All • Tasks • Jobs • Appointments) + View Switcher (Calendar • List)
// - Controlled: parent owns tab + view (URL is SSOT in WorkPage)
// - Optional per-tab memory (disabled by default to avoid dueling sources)
// - A11y-first: proper roles, focus mgmt, keyboard navigation

import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';

/* -------------------------------- utils -------------------------------- */

const cx = (...args) =>
  args
    .flatMap((a) =>
      typeof a === 'string'
        ? a
        : a && typeof a === 'object'
        ? Object.keys(a).filter((k) => a[k])
        : []
    )
    .join(' ')
    .trim();

const nf = (n) =>
  new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 }).format(
    Math.max(0, Number.isFinite(n) ? n : 0)
  );

const safeLocal = {
  get(key) {
    try {
      return window.localStorage.getItem(key);
    } catch {
      return null;
    }
  },
  set(key, val) {
    try {
      window.localStorage.setItem(key, val);
    } catch {}
  },
};

/* -------------------------------- icons -------------------------------- */

const CalendarIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path d="M7 2v4M17 2v4M3 10h18M6 4h12a3 3 0 0 1 3 3v12a3 3 0 0 1-3 3H6a3 3 0 0 1-3-3V7a3 3 0 0 1 3-3z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);
const ListIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="none" aria-hidden="true" {...props}>
    <path d="M4 6h16M4 12h16M4 18h10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

/* -------------------------------- tokens -------------------------------- */

const TAB_ORDER = ['all', 'tasks', 'jobs', 'appointments'];
const TAB_LABELS = {
  all: 'All',
  tasks: 'Tasks',
  jobs: 'Jobs',
  appointments: 'Appointments',
};

const VIEW_ORDER = ['calendar', 'list'];
const VIEW_META = {
  calendar: { label: 'Calendar', Icon: CalendarIcon, testid: 'view-calendar', title: 'Calendar view' },
  list: { label: 'List', Icon: ListIcon, testid: 'view-list', title: 'List view' },
};

/* -------------------------- tab button primitive -------------------------- */

function TabButton({ id, active, label, count, onClick, compact }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-controls={`panel-${id}`}
      id={`tab-${id}`}
      onClick={onClick}
      data-testid={`tab-${id}`}
      className={cx(
        'relative inline-flex items-center gap-2 rounded-lg px-3 sm:px-4',
        compact ? 'py-1.5 text-sm' : 'py-2 text-sm',
        active
          ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
          : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500'
      )}
    >
      <span className="font-medium">{label}</span>
      {Number.isFinite(count) ? (
        <span
          className={cx(
            'inline-flex min-w-[1.25rem] items-center justify-center rounded-full px-1.5 text-xs font-semibold',
            active
              ? 'bg-white/20 text-white'
              : 'bg-slate-200 text-slate-700 dark:bg-slate-700 dark:text-slate-200'
          )}
          aria-hidden="true"
        >
          {nf(count)}
        </span>
      ) : null}
    </button>
  );
}

/* ----------------------- view switcher button primitive ----------------------- */

function ViewButton({ id, active, label, Icon, title, disabled, onClick, compact }) {
  return (
    <button
      type="button"
      aria-label={label}
      title={title || label}
      data-testid={`switch-${id}`}
      disabled={disabled}
      onClick={onClick}
      className={cx(
        'inline-flex items-center justify-center rounded-lg',
        compact ? 'h-9 w-9' : 'h-10 w-10',
        active
          ? 'bg-slate-900 text-white dark:bg-slate-100 dark:text-slate-900'
          : 'text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
        disabled && 'opacity-40 cursor-not-allowed'
      )}
    >
      <Icon className={cx(compact ? 'h-4 w-4' : 'h-5 w-5')} />
    </button>
  );
}

/* -------------------------------- component -------------------------------- */

export default function WorkViewSwitcher({
  tab,
  onTabChange,
  view,
  onViewChange,
  counts = {},
  rememberPerTab = false,           // ⬅ default OFF to avoid dueling SSOT
  storageKey = 'work.view',         // only used if rememberPerTab=true
  availableViews,
  views,                            // alias for availableViews
  compact = false,
  className,
}) {
  const activeTab = TAB_ORDER.includes(tab) ? tab : 'all';

  // Which view buttons to show
  const requestedViews = views || availableViews || VIEW_ORDER;
  const safeViews = useMemo(
    () => VIEW_ORDER.filter((v) => requestedViews.includes(v)),
    [requestedViews]
  );
  const firstAvailableView = safeViews[0] || 'calendar';

  // Optional per-tab memory (off by default)
  const keyFor = useCallback((t) => `${storageKey}:${t}`, [storageKey]);

  useEffect(() => {
    if (!rememberPerTab) return;
    const saved = safeLocal.get(keyFor(activeTab));
    const candidate = saved && safeViews.includes(saved) ? saved : firstAvailableView;
    if (candidate && candidate !== view) onViewChange?.(candidate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, rememberPerTab, keyFor, firstAvailableView, safeViews.join('|')]);

  useEffect(() => {
    if (!rememberPerTab || !view) return;
    safeLocal.set(keyFor(activeTab), view);
  }, [rememberPerTab, view, activeTab, keyFor]);

  const onTabClick = useCallback(
    (next) => {
      if (next === activeTab) return;
      onTabChange?.(next);
    },
    [activeTab, onTabChange]
  );

  const onViewClick = useCallback(
    (nextView) => {
      if (nextView === view || !safeViews.includes(nextView)) return;
      onViewChange?.(nextView);
    },
    [view, onViewChange, safeViews]
  );

  // Keyboard support for tablist
  const tablistRef = useRef(null);
  const handleTabListKey = useCallback(
    (e) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(e.key)) return;
      e.preventDefault();
      const tabs = Array.from(tablistRef.current?.querySelectorAll('[role="tab"]') || []);
      if (!tabs.length) return;
      const idx = tabs.findIndex((el) => el.getAttribute('aria-selected') === 'true');
      const last = tabs.length - 1;
      let nextIdx = idx;
      if (e.key === 'ArrowRight') nextIdx = idx === last ? 0 : idx + 1;
      if (e.key === 'ArrowLeft') nextIdx = idx <= 0 ? last : idx - 1;
      if (e.key === 'Home') nextIdx = 0;
      if (e.key === 'End') nextIdx = last;
      const nextEl = tabs[nextIdx];
      nextEl?.focus();
      const id = nextEl?.id?.replace('tab-', '');
      if (TAB_ORDER.includes(id)) onTabClick(id);
    },
    [onTabClick]
  );

  // Counts (safe)
  // NOTE: Hide the badge on "All" when the total is 0 (per request).
  const computedAll =
    Number.isFinite(counts.all) && counts.all >= 0
      ? counts.all
      : ['tasks', 'jobs', 'appointments'].reduce(
          (acc, k) => acc + (Number.isFinite(counts[k]) ? counts[k] : 0),
          0
        );

  const safeCounts = {
    all: computedAll > 0 ? computedAll : undefined, // ← hide zero on All
    tasks: Number.isFinite(counts.tasks) ? counts.tasks : undefined,
    jobs: Number.isFinite(counts.jobs) ? counts.jobs : undefined,
    appointments: Number.isFinite(counts.appointments) ? counts.appointments : undefined,
  };

  return (
    <div
      className={cx(
        'flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between',
        className
      )}
      data-testid="work-view-switcher"
    >
      {/* Tabs */}
      <div
        role="tablist"
        aria-label="Work type"
        className={cx(
          'inline-flex w-full sm:w-auto items-center gap-1 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/50 p-1'
        )}
        ref={tablistRef}
        onKeyDown={handleTabListKey}
      >
        {TAB_ORDER.map((id) => (
          <TabButton
            key={id}
            id={id}
            label={TAB_LABELS[id]}
            count={id === 'all' ? safeCounts.all : safeCounts[id]}
            active={activeTab === id}
            onClick={() => onTabClick(id)}
            compact={compact}
          />
        ))}
      </div>

      {/* View switcher (Calendar • List) */}
      <div
        className={cx(
          'inline-flex items-center gap-1 rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-950/50 p-1 self-start sm:self-auto'
        )}
        role="group"
        aria-label="View switcher"
      >
        {safeViews.map((v) => {
          const meta = VIEW_META[v];
          if (!meta) return null;
          return (
            <ViewButton
              key={v}
              id={v}
              active={view === v}
              label={meta.label}
              title={meta.title}
              Icon={meta.Icon}
              disabled={false}
              onClick={() => onViewClick(v)}
              compact={compact}
            />
          );
        })}
      </div>
    </div>
  );
}

WorkViewSwitcher.propTypes = {
  tab: PropTypes.oneOf(['all', 'tasks', 'jobs', 'appointments']).isRequired,
  onTabChange: PropTypes.func.isRequired,
  view: PropTypes.oneOf(['calendar', 'list']).isRequired,
  onViewChange: PropTypes.func.isRequired,
  counts: PropTypes.shape({
    all: PropTypes.number,
    tasks: PropTypes.number,
    jobs: PropTypes.number,
    appointments: PropTypes.number,
  }),
  rememberPerTab: PropTypes.bool, // default false
  storageKey: PropTypes.string,
  availableViews: PropTypes.arrayOf(PropTypes.oneOf(['calendar', 'list'])),
  views: PropTypes.arrayOf(PropTypes.oneOf(['calendar', 'list'])),
  compact: PropTypes.bool,
  className: PropTypes.string,
};
